import { AdjustmentsIcon, ChatIcon, PencilIcon } from "@heroicons/react/outline";
import { SaveIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";

import VerboseModeToggle from "../WorkflowBlock/VerboseModeToggle";
import TemplateSelector from "../PromptTemplateBlock/TemplateSelector";
import {
  InlineEditorSection,
  ModeToggle,
  PromptMode,
  SaveToRegistryDialog,
} from "../../../InlineTemplateEditor";

import { LoopConfiguration } from "./types";

import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { Button } from "@/components/ui/button";
import PromptVersionLabelSelector from "@/components/ui/VersionLabelSelector";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import {
  Entity,
  EntityType,
  getEntityNumericId,
  useCreatePromptTemplate,
  usePrompt,
} from "@/queries";
import { ListPromptVersion } from "@/types";
import { InlineTemplate } from "@/types/evaluate";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface ReleaseLabel {
  id?: number;
  name: string;
}

interface PromptLoopConfigProps {
  promptName?: string;
  promptId?: number;
  versionNumber?: number | null;
  label?: string;
  verbose?: boolean;
  promptTemplateVariableMappings?: Record<string, string>;
  inlineTemplate?: InlineTemplate;
  onConfigChange: (config: LoopConfiguration["prompt_config"]) => void;
  readonly?: boolean;
  showVerboseMode?: boolean;
}

const PromptLoopConfig = observer(
  ({
    promptName,
    promptId,
    versionNumber,
    label,
    verbose,
    promptTemplateVariableMappings,
    inlineTemplate,
    onConfigChange,
    readonly,
    showVerboseMode = true,
  }: PromptLoopConfigProps) => {
    const auth = useAuth();
    const userToken = auth?.userToken || "";
    const user = useUser();
    const workspaceId = user?.activeWorkspaceId || 0;

    // Determine initial mode based on configuration
    const [mode, setMode] = useState<PromptMode>(() => {
      if (inlineTemplate?.prompt_template) {
        return "inline";
      }
      return "registry";
    });

    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const createPromptMutation = useCreatePromptTemplate(userToken);

    const shouldLookupPrompt = mode === "registry" && !!promptName && !promptId;
    const promptQuery = usePrompt(userToken, {
      workspace_id: workspaceId,
      prompt_name: shouldLookupPrompt ? promptName : undefined,
      number: versionNumber ?? undefined,
      label: label,
    });

    const resolvedPromptId = useMemo(() => {
      if (promptId) {
        return promptId;
      }
      if (promptQuery.data?.prompt_id) {
        return promptQuery.data.prompt_id;
      }
      return undefined;
    }, [promptId, promptQuery.data?.prompt_id]);

    useEffect(() => {
      if (mode === "registry" && promptName && !promptId && resolvedPromptId && onConfigChange) {
        onConfigChange({
          template: {
            id: resolvedPromptId,
            name: promptName,
            version_number: versionNumber ?? undefined,
            label: label,
          },
          prompt_template_variable_mappings: promptTemplateVariableMappings || {},
          verbose: verbose ?? false,
        });
      }
    }, [
      mode,
      promptName,
      promptId,
      resolvedPromptId,
      versionNumber,
      label,
      promptTemplateVariableMappings,
      verbose,
      onConfigChange,
    ]);

    const handleModeChange = useCallback(
      (newMode: PromptMode) => {
        setMode(newMode);
        if (newMode === "inline") {
          // Switch to inline mode - clear registry template and set up inline
          onConfigChange({
            template: undefined,
            inline_template: inlineTemplate || {
              prompt_template: {
                type: "chat",
                input_variables: [],
                messages: [
                  {
                    role: "user",
                    content: [{ type: "text", text: "" }],
                    template_format: "jinja2",
                  },
                ],
              },
            },
            prompt_template_variable_mappings: promptTemplateVariableMappings || {},
            verbose: verbose ?? false,
          });
        } else {
          // Switch to registry mode - clear inline template
          onConfigChange({
            template: resolvedPromptId
              ? {
                  id: resolvedPromptId,
                  name: promptName || "",
                  version_number: versionNumber ?? undefined,
                  label: label,
                }
              : undefined,
            inline_template: undefined,
            prompt_template_variable_mappings: promptTemplateVariableMappings || {},
            verbose: verbose ?? false,
          });
        }
      },
      [
        inlineTemplate,
        promptTemplateVariableMappings,
        verbose,
        resolvedPromptId,
        promptName,
        versionNumber,
        label,
        onConfigChange,
      ],
    );

    const handleInlineTemplateChange = useCallback(
      (template: InlineTemplate) => {
        onConfigChange({
          template: undefined,
          inline_template: template,
          prompt_template_variable_mappings: promptTemplateVariableMappings || {},
          verbose: verbose ?? false,
        });
      },
      [onConfigChange, promptTemplateVariableMappings, verbose],
    );

    const getInitialSelectionType = (): "version" | "label" => {
      if (versionNumber) {
        return "version";
      }
      if (label) {
        return "label";
      }
      return "version";
    };

    const initialSelection = {
      type: getInitialSelectionType(),
      version: versionNumber ? ({ number: versionNumber } as unknown as ListPromptVersion) : null,
      label: label ? ({ name: label } as ReleaseLabel) : null,
    };

    const handleTemplateSelection = useCallback(
      (selection: Entity<EntityType.PROMPT | EntityType.SNIPPET>) => {
        onConfigChange({
          template: {
            id: getEntityNumericId(selection.id),
            name: selection.name,
          },
          inline_template: undefined,
          prompt_template_variable_mappings: {},
          verbose: verbose ?? false,
        });
      },
      [onConfigChange, verbose],
    );

    const handleVersionLabelChange = useCallback(
      (selection: {
        type: "version" | "label";
        version?: ListPromptVersion | null;
        label?: ReleaseLabel | null;
      }) => {
        const template: LoopConfiguration["prompt_config"] = {
          template: {
            id: resolvedPromptId,
            name: promptName || "",
          },
          inline_template: undefined,
          prompt_template_variable_mappings: promptTemplateVariableMappings || {},
          verbose: verbose ?? false,
        };

        if (
          selection.type === "version" &&
          selection.version?.number !== undefined &&
          template.template
        ) {
          template.template.version_number = selection.version.number;
        }

        if (
          selection.type === "label" &&
          selection.label?.name !== undefined &&
          template.template
        ) {
          template.template.label = selection.label.name;
        }

        onConfigChange(template);
      },
      [onConfigChange, resolvedPromptId, promptName, promptTemplateVariableMappings, verbose],
    );

    const handleVerboseChange = useCallback(
      (enabled: boolean) => {
        if (mode === "inline") {
          onConfigChange({
            template: undefined,
            inline_template: inlineTemplate,
            prompt_template_variable_mappings: promptTemplateVariableMappings || {},
            verbose: enabled,
          });
        } else {
          onConfigChange({
            template: {
              id: resolvedPromptId,
              name: promptName || "",
              version_number: versionNumber,
              label: label,
            },
            inline_template: undefined,
            prompt_template_variable_mappings: promptTemplateVariableMappings || {},
            verbose: enabled,
          });
        }
      },
      [
        mode,
        inlineTemplate,
        resolvedPromptId,
        promptName,
        versionNumber,
        label,
        promptTemplateVariableMappings,
        onConfigChange,
      ],
    );

    const handleSaveToRegistry = useCallback(
      (name: string) =>
        new Promise<(() => void) | void>((resolve, reject) => {
          if (!inlineTemplate?.prompt_template || !workspaceId) {
            resolve();
            return;
          }

          createPromptMutation.mutate(
            {
              prompt_template: {
                prompt_name: name,
                workspace_id: workspaceId,
              },
              prompt_version: {
                prompt_template: inlineTemplate.prompt_template,
                metadata: inlineTemplate.metadata || undefined,
                provider_base_url_name: inlineTemplate.provider_base_url_name || null,
                provider_id: inlineTemplate.provider_id || undefined,
              },
            },
            {
              onSuccess: data => {
                if ("success" in data && !data.success) {
                  displayErrorToast("Failed to save prompt template");
                  reject(new Error("Failed to save prompt template"));
                  return;
                }

                displayToast("Prompt saved to registry", ToastType.success);

                // Switch to registry mode and select the new prompt
                const promptVersion = "prompt_version" in data ? data.prompt_version : null;
                resolve(() => {
                  if (promptVersion) {
                    setMode("registry");
                    onConfigChange({
                      template: {
                        id: promptVersion.prompt_id,
                        name: name,
                        version_number: promptVersion.version_number,
                      },
                      inline_template: undefined,
                      prompt_template_variable_mappings: promptTemplateVariableMappings || {},
                      verbose: verbose ?? false,
                    });
                  }
                  setSaveDialogOpen(false);
                });
              },
              onError: () => {
                displayErrorToast("Failed to save prompt template");
                reject(new Error("Failed to save prompt template"));
              },
            },
          );
        }),
      [
        inlineTemplate,
        workspaceId,
        createPromptMutation,
        onConfigChange,
        promptTemplateVariableMappings,
        verbose,
      ],
    );

    const hasError = promptQuery.error;

    const getErrorMessage = (error: unknown): string => {
      if (error && typeof error === "object" && "message" in error) {
        const { message } = error as { message: unknown };
        if (typeof message === "string") {
          return message;
        }
      }
      return "Failed to load prompt.";
    };

    const canSaveInline = mode === "inline" && inlineTemplate?.prompt_template && !readonly;

    return (
      <div className="space-y-4">
        <SectionGroup
          title={mode === "inline" ? "Inline Template" : "Prompt Template Selection"}
          icon={
            mode === "inline" ? (
              <PencilIcon className="h-4 w-4" />
            ) : (
              <ChatIcon className="h-4 w-4" />
            )
          }
          headerAction={
            !readonly && (
              <div className="flex items-center gap-2">
                {canSaveInline && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSaveDialogOpen(true)}
                    className="gap-1.5 text-xs"
                  >
                    <SaveIcon className="h-3.5 w-3.5" />
                    Save to Registry
                  </Button>
                )}
                <ModeToggle mode={mode} onModeChange={handleModeChange} disabled={readonly} />
              </div>
            )
          }
        >
          <div className="space-y-4">
            {mode === "registry" ? (
              <>
                <TemplateSelector
                  template={
                    resolvedPromptId && promptName
                      ? {
                          id: resolvedPromptId,
                          name: promptName,
                          version_number: versionNumber ?? undefined,
                          label: label,
                        }
                      : undefined
                  }
                  onSelect={handleTemplateSelection}
                  isLoading={!!promptName && !promptId && promptQuery.isLoading}
                  hasError={!!hasError}
                  errorMessage={hasError ? getErrorMessage(hasError) : undefined}
                />

                {resolvedPromptId && (
                  <PromptVersionLabelSelector
                    promptId={resolvedPromptId}
                    initialSelection={initialSelection}
                    onSelectionChange={handleVersionLabelChange}
                  />
                )}
              </>
            ) : (
              <InlineEditorSection
                template={inlineTemplate}
                onTemplateChange={handleInlineTemplateChange}
                readonly={readonly}
              />
            )}
          </div>
        </SectionGroup>

        {showVerboseMode && (
          <SectionGroup
            title="Advanced Settings"
            variant="compact"
            collapsible
            defaultExpanded={false}
            icon={<AdjustmentsIcon className="h-4 w-4" />}
          >
            <VerboseModeToggle
              enabled={verbose || false}
              onChange={handleVerboseChange}
              showVerboseMode={showVerboseMode}
              isLoading={false}
            />
          </SectionGroup>
        )}

        {/* Dialog for saving inline template to registry */}
        <SaveToRegistryDialog
          isOpen={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          onSave={handleSaveToRegistry}
        />
      </div>
    );
  },
);

export default PromptLoopConfig;
